import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
    CaretLeft, CaretRight, FileCsv, FileXls, CalendarBlank, Clock, Users, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatShortDate, weekdayLabel, startOfWeek, addDays, todayLocalDate } from "@/lib/time";
const ALL = "all";

export default function WeeklySummary() {
    const [weekStart, setWeekStart] = useState(() => startOfWeek(todayLocalDate()));
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [techFilter, setTechFilter] = useState(ALL);
    const [siteFilter, setSiteFilter] = useState(ALL);
    const [subFilter, setSubFilter] = useState(ALL);

    const [techs, setTechs] = useState([]);
    const [sites, setSites] = useState([]);
    const [filterSubs, setFilterSubs] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openSite, setOpenSite] = useState(null);

    const range = useMemo(() => {
        if (customFrom && customTo) return { from: customFrom, to: customTo, custom: true };
        return { from: weekStart, to: addDays(weekStart, 6), custom: false };
    }, [weekStart, customFrom, customTo]);

    const queryString = useMemo(() => {
        const p = new URLSearchParams({ date_from: range.from, date_to: range.to });
        if (techFilter !== ALL) p.set("technician_id", techFilter);
        if (siteFilter !== ALL) p.set("site_id", siteFilter);
        if (subFilter !== ALL) p.set("sublocation_id", subFilter);
        return p.toString();
    }, [range, techFilter, siteFilter, subFilter]);

    useEffect(() => {
        let cancelled = false;
        Promise.all([api.get("/users?role=technician"), api.get("/sites")])
            .then(([t, s]) => {
                if (cancelled) return;
                setTechs(Array.isArray(t.data) ? t.data : []);
                setSites(Array.isArray(s.data) ? s.data : []);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (siteFilter === ALL) { setFilterSubs([]); setSubFilter(ALL); return; }
        let cancelled = false;
        api.get(`/sites/${siteFilter}/sublocations`)
            .then(({ data: d }) => { if (!cancelled) setFilterSubs(Array.isArray(d) ? d : []); })
            .catch(() => {});
        setSubFilter(ALL);
        return () => { cancelled = true; };
    }, [siteFilter]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/time/weekly-summary?${queryString}`)
            .then(({ data: d }) => { if (!cancelled) setData(d); })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [queryString]);

    const shiftWeek = useCallback((delta) => {
        setCustomFrom(""); setCustomTo("");
        setWeekStart((w) => addDays(w, delta * 7));
    }, []);

    const download = async (format) => {
        try {
            const { data: blob } = await api.get(
                `/time/weekly-summary/export?format=${format}&${queryString}`,
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `AdvanceNetworks_WeeklyAttendance_${range.from}_to_${range.to}.${format}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            toast.success(`${format.toUpperCase()} exported`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const dates = Array.isArray(data?.dates) ? data.dates : [];
    const employees = Array.isArray(data?.employees) ? data.employees : [];
    const siteRows = Array.isArray(data?.sites) ? data.sites : [];

    return (
        <div className="space-y-6" data-testid="weekly-summary-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                        Admin · Attendance
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Weekly Attendance Summary
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm max-w-2xl">
                        Actual recorded time from Clock In / Clock Out records. No rounding, overtime or
                        billable-hour rules are applied — payroll and invoicing decisions stay with management.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => download("csv")}
                        className="h-10 border-[#003366] text-[#003366] hover:bg-slate-50"
                        data-testid="export-weekly-csv-button">
                        <FileCsv size={16} className="mr-1.5" /> CSV
                    </Button>
                    <Button variant="outline" onClick={() => download("xlsx")}
                        className="h-10 border-[#003366] text-[#003366] hover:bg-slate-50"
                        data-testid="export-weekly-excel-button">
                        <FileXls size={16} className="mr-1.5" /> Excel
                    </Button>
                </div>
            </div>

            {/* Week navigation + filters */}
            <Card className="border-[#E2E8F0]" data-testid="weekly-filters-card">
                <CardContent className="p-4 space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="outline" onClick={() => shiftWeek(-1)} className="h-10 px-3"
                            data-testid="prev-week-button">
                            <CaretLeft size={16} />
                        </Button>
                        <div className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] bg-slate-50 px-4 h-10">
                            <CalendarBlank size={16} className="text-[#0066D9]" />
                            <span className="text-sm font-semibold text-[#003366]" data-testid="week-range-label">
                                {formatShortDate(range.from)} — {formatShortDate(range.to)}
                            </span>
                            {range.custom && (
                                <Badge className="bg-slate-200 text-slate-700 text-[10px] uppercase">Custom</Badge>
                            )}
                        </div>
                        <Button variant="outline" onClick={() => shiftWeek(1)} className="h-10 px-3"
                            data-testid="next-week-button">
                            <CaretRight size={16} />
                        </Button>
                        <Button variant="ghost"
                            onClick={() => { setCustomFrom(""); setCustomTo(""); setWeekStart(startOfWeek(todayLocalDate())); }}
                            className="h-10 text-[#0066D9]" data-testid="this-week-button">
                            This week
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Custom from</Label>
                            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                                className="h-10" data-testid="weekly-custom-from" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Custom to</Label>
                            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                                className="h-10" data-testid="weekly-custom-to" />
                            {(!!customFrom !== !!customTo) && (
                                <p className="text-[11px] text-amber-700">Set both dates to apply a custom range.</p>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Employee</Label>
                            <Select value={techFilter} onValueChange={setTechFilter}>
                                <SelectTrigger className="h-10" data-testid="weekly-filter-technician"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL}>All employees</SelectItem>
                                    {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Site</Label>
                            <Select value={siteFilter} onValueChange={setSiteFilter}>
                                <SelectTrigger className="h-10" data-testid="weekly-filter-site"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL}>All sites</SelectItem>
                                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Data Hall</Label>
                            <Select value={subFilter} onValueChange={setSubFilter} disabled={siteFilter === ALL}>
                                <SelectTrigger className="h-10" data-testid="weekly-filter-sublocation"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL}>All</SelectItem>
                                    {filterSubs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Totals */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border-[#E2E8F0] bg-[#003366] text-white" data-testid="weekly-total-card">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/70">
                            <Clock size={16} /> Total recorded time
                        </div>
                        <div className="text-3xl font-bold mt-2" style={{ fontFamily: "Outfit" }} data-testid="weekly-total-display">
                            {data?.totals?.display || "0h 00m"}
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-[#E2E8F0]" data-testid="weekly-employees-card">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                            <Users size={16} /> Employees with time
                        </div>
                        <div className="text-3xl font-bold mt-2 text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            {data?.totals?.employees ?? 0}
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-[#E2E8F0]" data-testid="weekly-shifts-card">
                    <CardContent className="p-5">
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Shifts · open</div>
                        <div className="text-3xl font-bold mt-2 text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            {data?.totals?.shifts ?? 0}
                            <span className="text-base font-semibold text-amber-700 ml-2">
                                {(data?.totals?.open_shifts ?? 0) > 0 ? `· ${data.totals.open_shifts} open` : ""}
                            </span>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Tabs defaultValue="employee">
                <TabsList data-testid="weekly-tabs">
                    <TabsTrigger value="employee" data-testid="tab-by-employee">By employee</TabsTrigger>
                    <TabsTrigger value="site" data-testid="tab-by-site">By site</TabsTrigger>
                    <TabsTrigger value="datahall" data-testid="tab-by-datahall">By Data Hall</TabsTrigger>
                </TabsList>

                {/* ---- Employee × day grid ---- */}
                <TabsContent value="employee">
                    <Card className="border-[#E2E8F0]">
                        <CardContent className="p-0">
                            {loading ? (
                                <div className="p-10 text-center text-slate-500">Loading summary…</div>
                            ) : employees.length === 0 ? (
                                <div className="p-12 text-center text-slate-500" data-testid="weekly-employee-empty">
                                    No time recorded in this period.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-slate-50">
                                                <TableHead className="sticky left-0 bg-slate-50">Employee</TableHead>
                                                {dates.map((d) => (
                                                    <TableHead key={d} className="text-center whitespace-nowrap">
                                                        <div>{weekdayLabel(d)}</div>
                                                        <div className="text-[10px] font-normal text-slate-400">{formatShortDate(d)}</div>
                                                    </TableHead>
                                                ))}
                                                <TableHead className="text-right">Total</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {employees.map((emp) => (
                                                <TableRow key={emp.technician_id} data-testid={`weekly-row-${emp.technician_id}`}>
                                                    <TableCell className="font-semibold text-[#003366] text-sm sticky left-0 bg-white whitespace-nowrap">
                                                        {emp.technician_name}
                                                    </TableCell>
                                                    {dates.map((d) => {
                                                        const cell = emp.days?.[d];
                                                        if (!cell) {
                                                            return (
                                                                <TableCell key={d} className="text-center text-slate-300"
                                                                    data-testid={`cell-${emp.technician_id}-${d}`}>—</TableCell>
                                                            );
                                                        }
                                                        return (
                                                            <TableCell key={d}
                                                                className={`text-center text-sm font-mono whitespace-nowrap ${cell.open ? "text-amber-800 bg-amber-50" : "text-[#003366]"}`}
                                                                data-testid={`cell-${emp.technician_id}-${d}`}>
                                                                {cell.display || "0h 00m"}
                                                                {cell.open && (
                                                                    <div className="text-[9px] uppercase tracking-wider font-semibold">open</div>
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}
                                                    <TableCell className="text-right font-bold text-[#003366] whitespace-nowrap"
                                                        data-testid={`weekly-total-${emp.technician_id}`}>
                                                        {emp.total_display}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <div className="px-4 py-3 border-t border-slate-100 flex flex-wrap gap-4 text-xs text-slate-500">
                                        <span><span className="text-slate-300 font-mono">—</span> no shift recorded</span>
                                        <span><span className="font-mono text-[#003366]">9h 30m</span> completed</span>
                                        <span className="text-amber-800 bg-amber-50 px-1.5 rounded">open / incomplete shift (not counted as a full day)</span>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ---- Site totals ---- */}
                <TabsContent value="site">
                    <Card className="border-[#E2E8F0]">
                        <CardContent className="p-4 space-y-3">
                            {siteRows.length === 0 ? (
                                <div className="p-8 text-center text-slate-500" data-testid="weekly-site-empty">
                                    No time recorded in this period.
                                </div>
                            ) : siteRows.map((s) => (
                                <div key={s.site_id} className="rounded-lg border border-[#E2E8F0] overflow-hidden"
                                    data-testid={`weekly-site-${s.site_id}`}>
                                    <button
                                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                                        onClick={() => setOpenSite(openSite === s.site_id ? null : s.site_id)}
                                        data-testid={`weekly-site-toggle-${s.site_id}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <CaretDown size={14}
                                                className={`text-slate-400 transition-transform ${openSite === s.site_id ? "rotate-0" : "-rotate-90"}`} />
                                            <span className="font-bold text-[#003366]">{s.site_name}</span>
                                            {s.open_shifts > 0 && (
                                                <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase">
                                                    {s.open_shifts} open
                                                </Badge>
                                            )}
                                        </div>
                                        <span className="font-mono font-semibold text-[#003366]">{s.total_display}</span>
                                    </button>
                                    {openSite === s.site_id && (
                                        <div className="border-t border-slate-100 divide-y divide-slate-50">
                                            {s.employees.map((e) => (
                                                <div key={e.technician_id} className="flex items-center justify-between px-4 py-2 pl-10 text-sm"
                                                    data-testid={`weekly-site-emp-${s.site_id}-${e.technician_id}`}>
                                                    <span className="text-slate-700">{e.technician_name}</span>
                                                    <span className="font-mono text-slate-600">{e.total_display}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ---- Data Hall totals ---- */}
                <TabsContent value="datahall">
                    <Card className="border-[#E2E8F0]">
                        <CardContent className="p-4 space-y-5">
                            {siteRows.length === 0 ? (
                                <div className="p-8 text-center text-slate-500" data-testid="weekly-datahall-empty">
                                    No time recorded in this period.
                                </div>
                            ) : siteRows.map((s) => (
                                <div key={s.site_id} data-testid={`weekly-datahall-site-${s.site_id}`}>
                                    <div className="flex items-center justify-between pb-2 mb-1 border-b border-[#E2E8F0]">
                                        <span className="font-bold text-[#003366]">{s.site_name}</span>
                                        <span className="font-mono font-semibold text-[#003366]">{s.total_display}</span>
                                    </div>
                                    <div className="divide-y divide-slate-50">
                                        {s.sublocations.map((sub, i) => (
                                            <div key={sub.sublocation_id || `none-${i}`}
                                                className="flex items-center justify-between py-2 text-sm"
                                                data-testid={`weekly-datahall-row-${s.site_id}-${i}`}>
                                                <div className="min-w-0">
                                                    <span className="text-slate-700">{sub.name}</span>
                                                    {sub.job_code && (
                                                        <span className="ml-2 text-xs font-mono text-slate-400">
                                                            Job code: {sub.job_code}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="font-mono text-slate-600 whitespace-nowrap">{sub.total_display}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
